<template>
  <v-dialog
    :model-value="show"
    max-width="500"
    @update:model-value="$emit('update:show', $event)"
  >
    <v-card>
      <v-toolbar color="primary" density="compact">
        <v-toolbar-title>Rapport Filters</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="handleClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-4">
        <!-- Date picker -->
        <v-menu
          v-model="datePicker"
          :close-on-content-click="false"
          transition="scale-transition"
          min-width="auto"
        >
          <template #activator="{ props }">
            <v-text-field
              :model-value="dateDisplay"
              label="Datum rapport"
              prepend-icon="mdi-calendar"
              variant="outlined"
              density="comfortable"
              class="mb-3"
              clearable
              v-bind="props"
              readonly
              @click:clear="clearDate"
            ></v-text-field>
          </template>

          <v-date-picker v-model="dateValue" @update:model-value="setDate">
            <v-spacer></v-spacer>
            <v-btn text color="warning" @click="clearDate">Clear</v-btn>
            <v-btn text color="primary" @click="datePicker = false">OK</v-btn>
          </v-date-picker>
        </v-menu>

        <!-- User select -->
        <v-select
          v-model="userValue"
          :items="userOptions"
          item-title="title"
          item-value="value"
          label="Eigenaar rapport"
          prepend-icon="mdi-account"
          variant="outlined"
          density="comfortable"
          class="mb-3"
          clearable
        ></v-select>

        <!-- Report Type select -->
        <v-select
          v-model="categoryValue"
          :items="categoryOptions"
          item-title="title"
          item-value="value"
          label="Type rapport"
          multiple
          prepend-icon="mdi-format-list-checks"
          variant="outlined"
          density="comfortable"
          class="mb-3"
          clearable
        ></v-select>

        <!-- Important filter -->
        <v-select
          v-model="importantValue"
          :items="importantOptions"
          item-title="title"
          item-value="value"
          label="Belangrijke rapporten"
          prepend-icon="mdi-alert-octagon"
          variant="outlined"
          density="comfortable"
          class="mb-3"
          clearable
        ></v-select>

        <!-- Contact filters -->
        <div v-if="taggedContacts.length > 0" class="mb-4">
          <span class="text-subtitle-1 d-block mb-2">Getagde Contacten</span>
          <v-select
            v-model="selectedContactIds"
            :items="contactOptions"
            item-title="title"
            item-value="value"
            label="Filter op contacten"
            multiple
            prepend-icon="mdi-account-multiple"
            variant="outlined"
            density="comfortable"
            class="mb-2"
            clearable
          >
            <template #item="{ props, item }">
              <v-list-item v-bind="props">
                <template #prepend>
                  <v-badge
                    :color="getContactBadgeColor(item.raw.contact)"
                    :content="item.raw.contact.usage_count"
                    inline
                  >
                    <v-icon size="small">mdi-account</v-icon>
                  </v-badge>
                </template>
              </v-list-item>
            </template>
          </v-select>
        </div>

        <!-- Improved Trait filters with better UI -->
        <div class="mb-4">
          <div class="d-flex align-center justify-space-between mb-3">
            <span class="text-subtitle-1">Kenmerken</span>
            <v-btn-toggle
              v-model="traitsAnyMode"
              variant="outlined"
              size="small"
              mandatory
            >
              <v-btn :value="true" size="small">Enkel</v-btn>
              <v-btn :value="false" size="small">Alle</v-btn>
            </v-btn-toggle>
          </div>

          <ReportTraitPicker
            v-model="selectedTraitIds"
            :title="
              traitsAnyMode
                ? 'Enkel kenmerk aanwezig'
                : 'Alle kenmerken aanwezig'
            "
            class="mb-2"
          />
        </div>

        <v-row>
          <v-col cols="6">
            <v-btn
              block
              color="error"
              variant="outlined"
              prepend-icon="mdi-filter-remove"
              @click="clearFilters"
            >
              Wissen
            </v-btn>
          </v-col>
          <v-col cols="6">
            <v-btn
              block
              color="primary"
              prepend-icon="mdi-filter-check"
              @click="applyFilters"
            >
              Toepassen
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted } from "vue";
import moment from "moment";
import { useReportCategoriesStoreRefactor } from "@/stores/reportCategoriesStoreRefactor";
import { useReportFilterStore } from "@/stores/reportFilterStore";
import { useReportCategoryIcons } from "@/composables/useReportCategoryIcons";
import { useUsersStoreRefactor } from "@/stores/usersStoreRefactor";
import { useAccountsStoreRefactor } from "@/stores/accountsStoreRefactor";
import ReportTraitPicker from "./ReportTraitPicker.vue";

export default defineComponent({
  name: "ReportFilterDialog",
  components: { ReportTraitPicker },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    account_id: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ["update:show", "search"],

  setup(props, { emit }) {
    // Stores
    const reportCategoriesStore = useReportCategoriesStoreRefactor();
    const reportFilterStore = useReportFilterStore();
    const usersStore = useUsersStoreRefactor();
    const accountsStore = useAccountsStoreRefactor();

    // Composables
    const { ensureCategoriesLoaded } = useReportCategoryIcons();

    // Local state for dialog (completely independent from store until submit)
    const datePicker = ref(false);
    const dateValue = ref("");
    const userValue = ref("");
    const categoryValue = ref([]);
    const importantValue = ref("");
    const traitsAnyMode = ref(true);
    const selectedTraitIds = ref([]); // Single array for both modes
    const selectedContactIds = ref([]); // Array of contact IDs
    const account = ref(null);

    // Helper to extract trait IDs from store cache format
    const parseTraitIds = (cacheArray) => {
      if (!Array.isArray(cacheArray)) return [];
      return cacheArray
        .map((s) => s.replace(/,/g, ""))
        .map(Number)
        .filter((n) => n > 0);
    };

    const parseTraitString = (cacheString) => {
      if (typeof cacheString !== "string") return [];
      return cacheString
        .split(",")
        .map(Number)
        .filter((n) => n > 0);
    };

    // Load account data when account_id is provided
    watch(
      () => props.account_id,
      async (accountId) => {
        if (accountId) {
          try {
            const accountData = await accountsStore.fetchById(accountId);
            account.value = accountData;
          } catch (error) {
            console.error("Error loading account for contact filter:", error);
            account.value = null;
          }
        } else {
          account.value = null;
        }
      },
      { immediate: true },
    );

    // Initialize from store when dialog opens
    watch(
      () => props.show,
      (isShowing) => {
        if (isShowing) {
          const filters = reportFilterStore.filters;
          dateValue.value = filters.day_eq || "";
          userValue.value = filters.user_id_eq || "";
          categoryValue.value = filters.report_category_name_in || [];
          importantValue.value = filters.important_eq || "";
          traitsAnyMode.value = reportFilterStore.traitsAnyMode;

          // Parse trait IDs from either format
          if (
            reportFilterStore.traitsAnyMode &&
            filters.trait_ids_cache_cont_any?.length
          ) {
            selectedTraitIds.value = parseTraitIds(
              filters.trait_ids_cache_cont_any,
            );
          } else if (
            !reportFilterStore.traitsAnyMode &&
            filters.trait_ids_cache_cont
          ) {
            selectedTraitIds.value = parseTraitString(
              filters.trait_ids_cache_cont,
            );
          } else {
            selectedTraitIds.value = [];
          }

          // Initialize contact IDs from store
          selectedContactIds.value = reportFilterStore.selectedContactIds || [];
        }
      },
    );

    // No longer needed - filter store handles state directly

    // Format date for display
    const dateDisplay = computed(() => {
      if (!dateValue.value) return "";
      return moment(dateValue.value).format("DD-MM-YYYY");
    });

    // User options for select - dynamically from users store
    const userOptions = computed(() => {
      const activeUsers = usersStore.activeUsersInCurrentTenant || [];
      return [
        { title: "Alle gebruikers", value: "" },
        ...activeUsers.map((user) => ({
          title: `${user.first_name} ${user.last_name}`,
          value: user.id,
        })),
      ];
    });

    // Important options for select
    const importantOptions = ref([
      { title: "Alle rapporten", value: "" },
      { title: "Alleen belangrijke", value: true },
      { title: "Alleen niet-belangrijke", value: false },
    ]);

    // Category options from report categories store
    const categoryOptions = computed(() => {
      return reportCategoriesStore.reportCategories.map((category) => ({
        title: category.name,
        value: category.name,
      }));
    });

    // Tagged contacts from account
    const taggedContacts = computed(() => {
      return account.value?.tagged_contacts_summary || [];
    });

    // Contact options for multi-select
    const contactOptions = computed(() => {
      return taggedContacts.value.map((contact) => ({
        title: contact.name,
        value: contact.id,
        contact: contact, // Store full contact object for badge color
      }));
    });

    // Get badge color for contact based on recency
    const getContactBadgeColor = (contact) => {
      if (!contact) return "grey";
      if (contact.recency_status === "green") return "success";
      if (contact.recency_status === "yellow") return "warning";
      return "error";
    };

    // No longer needed - filter store handles state management

    // Date handlers
    function setDate(value) {
      dateValue.value = value;
    }
    function clearDate() {
      dateValue.value = "";
    }

    // Build filters object from local state - dynamically create trait parameters
    const buildFilters = () => {
      const filters = {
        day_eq: dateValue.value || "",
        user_id_eq: userValue.value || "",
        report_category_name_in: categoryValue.value || [],
        description_cont: "", // Managed by search field
        important_eq: importantValue.value || "",
        trait_ids_cache_cont_any: [],
        trait_ids_cache_cont: "",
        contact_ids_cache_cont_any: [],
      };

      // Dynamically build trait parameters based on current mode and selection
      const validTraitIds = selectedTraitIds.value.filter((id) => id > 0);

      if (validTraitIds.length > 0) {
        if (traitsAnyMode.value) {
          // "Any" mode: each trait ID becomes ",id," for _cont_any array
          filters.trait_ids_cache_cont_any = validTraitIds.map(
            (id) => `,${id},`,
          );
        } else {
          // "All" mode: combine all IDs into single ",id1,id2,id3," string for _cont
          filters.trait_ids_cache_cont = `,${validTraitIds.join(",")},`;
        }
      }

      // Add contact IDs filter (format as cache strings like traits)
      const validContactIds = selectedContactIds.value
        .map(Number)
        .filter((id) => id > 0);
      if (validContactIds.length > 0) {
        // Format as ",id," cache strings for ransack
        filters.contact_ids_cache_cont_any = validContactIds.map((id) => `,${id},`);
      }

      return filters;
    };

    // Clear all filters
    const clearFilters = () => {
      dateValue.value = "";
      userValue.value = "";
      categoryValue.value = [];
      importantValue.value = "";
      traitsAnyMode.value = true;
      selectedTraitIds.value = [];
      selectedContactIds.value = [];

      reportFilterStore.applyFilters(buildFilters(), true);
      emit("search");
      handleClose();
    };

    // Apply filters
    const applyFilters = () => {
      reportFilterStore.applyFilters(buildFilters(), traitsAnyMode.value);
      emit("search");
      handleClose();
    };

    // Handle dialog close
    const handleClose = () => {
      reportFilterStore.closeDialog();
      emit("update:show", false);
    };

    // Load categories and users on mount
    onMounted(async () => {
      await ensureCategoriesLoaded();
      await usersStore.fetchAll();
    });

    return {
      datePicker,
      dateValue,
      dateDisplay,
      userValue,
      categoryValue,
      importantValue,
      userOptions,
      categoryOptions,
      importantOptions,
      traitsAnyMode,
      selectedTraitIds,
      selectedContactIds,
      taggedContacts,
      contactOptions,
      getContactBadgeColor,
      setDate,
      clearDate,
      clearFilters,
      applyFilters,
      handleClose,
    };
  },
});
</script>
